import { useEffect, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { QBOAccountResponse } from "../database";
import { ExistingGLAccount, GLAccount_CreateAPI } from "../models/gl-account";
import { User } from "firebase/auth";
import BaseButtonPrimary from "./BaseButtonPrimary";
import BaseButtonSecondary from "./BaseButtonSecondary";

// Define keywords and types for common accounts
const UNDEPOSITED_FUNDS_KEYWORDS = ["undeposited funds", "undeposited fund"];
const CHECKING_KEYWORDS = ["checking"];
const CHECKING_SUBTYPES = ["checking"];
const SAVINGS_SUBTYPES = ["savings"];
const BANK_ACCOUNT_TYPE = "Bank";
const INCOME_ACCOUNT_TYPES = ["Income"];
const SALES_KEYWORDS = ["sales", "revenue", "income", "service"];

interface CategorizedQBOAccount {
  account: QBOAccountResponse;
  priority: number; // 4 = highest (Checking/Undeposited Funds), 3 = Income, 2 = other keywords, 1 = default
  isPreselected?: boolean; // Future: for auto-selecting some common ones
}

interface ImportQBOAccountsDialogProps {
  isDialogOpen: boolean;
  closeDialog: () => void;
  siteKey: string;
  firebaseUser: User;
  currency: string;
  onImportAccounts: (accounts: GLAccount_CreateAPI[]) => Promise<void>;
  qboAccounts: QBOAccountResponse[] | null;
  existingGLAccounts: ExistingGLAccount[];
  isLoading: boolean;
}

export default function ImportQBOAccountsDialog({
  isDialogOpen,
  closeDialog,
  siteKey,
  firebaseUser,
  currency,
  onImportAccounts,
  qboAccounts,
  existingGLAccounts,
  isLoading,
}: ImportQBOAccountsDialogProps) {
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [categorizedAccounts, setCategorizedAccounts] = useState<
    CategorizedQBOAccount[]
  >([]);
  const [filteredCategorizedAccounts, setFilteredCategorizedAccounts] =
    useState<CategorizedQBOAccount[]>([]);

  // Reset selected accounts and search when dialog opens or accounts change
  useEffect(() => {
    setSelectedAccountIds([]);
    setSearchQuery("");
    // Categorize accounts when qboAccounts or isDialogOpen changes
    if (isDialogOpen && qboAccounts) {
      const newCategorizedAccounts = qboAccounts.map((acc) => {
        let priority = 1; // Default priority
        const accNameLower = acc.name.toLowerCase();
        const accType = acc.accountType;
        const accSubTypeLower = acc.accountSubType?.toLowerCase();

        // Priority 4: Checking or Undeposited Funds
        if (
          UNDEPOSITED_FUNDS_KEYWORDS.some((keyword) =>
            accNameLower.includes(keyword),
          ) ||
          CHECKING_KEYWORDS.some((keyword) => accNameLower.includes(keyword))
        ) {
          priority = 4;
        }
        // Priority 4: Bank accounts with Checking or Savings subtypes
        else if (accType === BANK_ACCOUNT_TYPE) {
          if (
            accSubTypeLower &&
            (CHECKING_SUBTYPES.includes(accSubTypeLower) ||
              SAVINGS_SUBTYPES.includes(accSubTypeLower))
          ) {
            priority = 4;
          }
        }
        // Priority 3: Income Account Types
        else if (INCOME_ACCOUNT_TYPES.includes(accType)) {
          priority = 3;
        }
        // Priority 2: Other keyword matches (Sales keywords)
        else if (
          SALES_KEYWORDS.some((keyword) => accNameLower.includes(keyword))
        ) {
          priority = 2;
        }

        return { account: acc, priority };
      });

      // Sort suggested to the top
      newCategorizedAccounts.sort((a, b) => {
        if (a.priority > b.priority) return -1;
        if (a.priority < b.priority) return 1;
        // Optional: further sort by name within suggested/non-suggested
        return a.account.name.localeCompare(b.account.name);
      });
      setCategorizedAccounts(newCategorizedAccounts);
      setFilteredCategorizedAccounts(newCategorizedAccounts); // Initialize filtered list
    } else if (!isDialogOpen) {
      setCategorizedAccounts([]); // Clear when dialog closes
      setFilteredCategorizedAccounts([]);
    }
  }, [qboAccounts, isDialogOpen]);

  // Filter accounts when search query changes
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredCategorizedAccounts(categorizedAccounts);
    } else {
      const lowercaseQuery = searchQuery.toLowerCase();
      const filtered = categorizedAccounts.filter(
        ({ account }) =>
          account.name.toLowerCase().includes(lowercaseQuery) ||
          (account.fullyQualifiedName &&
            account.fullyQualifiedName
              .toLowerCase()
              .includes(lowercaseQuery)) ||
          (account.accountSubType &&
            account.accountSubType.toLowerCase().includes(lowercaseQuery)),
      );
      setFilteredCategorizedAccounts(filtered);
    }
  }, [searchQuery, categorizedAccounts]);

  // Check if an account is already imported
  const isAccountAlreadyImported = (accountId: string): boolean => {
    return existingGLAccounts.some(
      (account) =>
        account.customData && account.customData.accountingRefID === accountId,
    );
  };

  // Find the matching GL account for a QBO account
  const findMatchingGLAccount = (
    accountId: string,
  ): ExistingGLAccount | undefined => {
    return existingGLAccounts.find(
      (account) =>
        account.customData && account.customData.accountingRefID === accountId,
    );
  };

  const handleToggleAccount = (accountId: string) => {
    // Don't allow toggling if already imported
    if (isAccountAlreadyImported(accountId)) return;

    setSelectedAccountIds((prevSelected) =>
      prevSelected.includes(accountId)
        ? prevSelected.filter((id) => id !== accountId)
        : [...prevSelected, accountId],
    );
  };

  const handleSelectAll = () => {
    if (!filteredCategorizedAccounts) return;

    // Get all IDs that are not already imported from the currently filtered list
    const selectableAccountIds = filteredCategorizedAccounts
      .filter(({ account }) => !isAccountAlreadyImported(account.id))
      .map(({ account }) => account.id);

    if (
      selectableAccountIds.length === selectedAccountIds.length &&
      selectableAccountIds.length > 0
    ) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(selectableAccountIds);
    }
  };

  const handleImportAccounts = async () => {
    if (!qboAccounts) return;

    setIsImporting(true);

    try {
      const accountsToImport = categorizedAccounts // Import from the original categorized list based on selected IDs
        .filter(({ account }) => selectedAccountIds.includes(account.id))
        .map(({ account }) => ({
          name: account.name,
          accountType: account.accountType,
          currency: currency,
          customData: {
            accountingRefID: account.id,
          },
          deleted: false,
          lastModifiedBy: firebaseUser.uid,
          siteKey: siteKey,
        }));

      await onImportAccounts(accountsToImport);
      closeDialog();
    } catch (error) {
      console.error("Error importing accounts:", error);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={closeDialog}
      aria-labelledby="import-qbo-accounts-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        id="import-qbo-accounts-dialog-title"
        className="flex items-center justify-between"
      >
        <span>Import QuickBooks Chart of Accounts</span>
        <IconButton
          aria-label="close"
          onClick={closeDialog}
          size="small"
          className="absolute right-2 top-2"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <CircularProgress />
            <span className="ml-4">Loading QuickBooks accounts...</span>
          </div>
        ) : qboAccounts && qboAccounts.length > 0 ? ( // Check original qboAccounts for initial load
          <>
            <div className="mb-4">
              <TextField
                fullWidth
                placeholder="Search accounts by name, type, or subtype..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <BaseButtonSecondary
                  type="button"
                  onClick={handleSelectAll}
                  disabled={
                    filteredCategorizedAccounts.length === 0 &&
                    searchQuery.length > 0
                  }
                >
                  {selectedAccountIds.length ===
                    filteredCategorizedAccounts.filter(
                      ({ account }) => !isAccountAlreadyImported(account.id),
                    ).length && selectedAccountIds.length > 0
                    ? "Deselect All Visible"
                    : "Select All Visible"}
                </BaseButtonSecondary>
              </div>
              <div>
                <span className="text-gray-700">
                  {selectedAccountIds.length} selected (of{" "}
                  {filteredCategorizedAccounts.length} visible). Total accounts:{" "}
                  {qboAccounts.length}.
                  {existingGLAccounts.length > 0 &&
                    ` Previously imported: ${existingGLAccounts.length}.`}
                </span>
              </div>
            </div>
            <TableContainer component={Paper} sx={{ maxHeight: "50vh" }}>
              {" "}
              {/* Added maxHeight for scroll */}
              <Table stickyHeader>
                {" "}
                {/* Added stickyHeader */}
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          filteredCategorizedAccounts.length > 0 && // Check if there are any accounts to select
                          selectedAccountIds.length ===
                            filteredCategorizedAccounts.filter(
                              ({ account }) =>
                                !isAccountAlreadyImported(account.id),
                            ).length
                        }
                        indeterminate={
                          selectedAccountIds.length > 0 &&
                          selectedAccountIds.length <
                            filteredCategorizedAccounts.filter(
                              ({ account }) =>
                                !isAccountAlreadyImported(account.id),
                            ).length
                        }
                        onChange={handleSelectAll}
                        onClick={(e) => e.stopPropagation()}
                        disabled={
                          filteredCategorizedAccounts.filter(
                            ({ account }) =>
                              !isAccountAlreadyImported(account.id),
                          ).length === 0
                        }
                      />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>SubType</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCategorizedAccounts.length > 0 ? (
                    filteredCategorizedAccounts.map(({ account }) => {
                      const isImported = isAccountAlreadyImported(account.id);
                      const matchingAccount = isImported
                        ? findMatchingGLAccount(account.id)
                        : undefined;

                      return (
                        <TableRow
                          key={account.id}
                          hover={!isImported}
                          onClick={() =>
                            !isImported && handleToggleAccount(account.id)
                          }
                          className={`${isImported ? "bg-gray-100 opacity-70" : ""}`}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={
                                isImported ||
                                selectedAccountIds.includes(account.id)
                              }
                              disabled={isImported}
                              onChange={() => handleToggleAccount(account.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {account.fullyQualifiedName || account.name}
                          </TableCell>
                          <TableCell>{account.accountType}</TableCell>
                          <TableCell>{account.accountSubType}</TableCell>
                          <TableCell align="right">
                            {account.currentBalance !== undefined
                              ? account.currentBalance.toLocaleString(
                                  undefined,
                                  {
                                    style: "currency",
                                    currency: currency,
                                  },
                                )
                              : "N/A"}
                          </TableCell>
                          <TableCell align="center">
                            {isImported && (
                              <Tooltip
                                title={`Already imported as "${matchingAccount?.name}"`}
                              >
                                <CheckCircleIcon
                                  color="success"
                                  fontSize="small"
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center">
                        <p>
                          No accounts match your search query: "{searchQuery}"
                        </p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <div className="py-8 text-center">
            <p>No QuickBooks accounts found or an error occurred.</p>
            <p className="text-sm text-gray-500">
              If you have accounts in QuickBooks, try refreshing or check your
              connection.
            </p>
          </div>
        )}
      </DialogContent>
      <div className="flex justify-end gap-2 p-4">
        <BaseButtonSecondary type="button" onClick={closeDialog}>
          Cancel
        </BaseButtonSecondary>
        <BaseButtonPrimary
          type="button"
          onClick={handleImportAccounts}
          disabled={selectedAccountIds.length === 0 || isImporting}
        >
          {isImporting ? (
            <>
              <CircularProgress size={20} className="mr-2" />
              Importing...
            </>
          ) : (
            `Import ${selectedAccountIds.length} Account${selectedAccountIds.length !== 1 ? "s" : ""}`
          )}
        </BaseButtonPrimary>
      </div>
    </Dialog>
  );
}
